import { useEffect, useState } from "react";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { apiFetch } from "../../utils/api";
import { showSuccess, showError } from "../../utils/dialog";
import { COUNTRY_NAME_TO_CODE } from "../../utils/countries";

const EditPayerModal = ({ isOpen, onClose, payer, onSaved }) => {
 const [form, setForm] = useState({
  name: "",
  legalName: "",
  workerType: "",
  address: {
    name: "",
    street: "",
    city: "",
    region: "",
    zip: "",
    country: "",
  },
});


  const [isSaving, setIsSaving] = useState(false);

 useEffect(() => {
  if (!payer) return;

  setForm({
    name: payer.name ?? "",
    legalName: payer.legal_name ?? "",
    workerType: payer.worker_type ?? "",
    address: {
      name: payer.address_name ?? "",
      street: payer.street ?? "",
      city: payer.city ?? "",
      region: payer.region ?? "",
      zip: payer.zip ?? "",
      country: payer.country ?? "",
    },
  });
}, [payer]);


  const handleChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

const handleSave = async (e) => {
  e.preventDefault();
  if (!payer) return;

  // 🔒 Normalize country EXACTLY like Add
  const countryCode =
    form.address.country?.length === 2
      ? form.address.country
      : COUNTRY_NAME_TO_CODE[form.address.country];

  if (!countryCode || countryCode.length !== 2) {
    showError("Country must be a valid 2-letter code");
    return;
  }

  if (!form.address.region || form.address.region.length !== 2) {
    showError("Province / State must be a 2-letter code");
    return;
  }

  // ✅ BUILD PAYLOAD (DO NOT SEND `form`)
  const payload = {
    name: form.name,
    legalName: form.legalName,
    workerType: form.workerType,
    address: {
      name: form.address.name,
      street: form.address.street,
      city: form.address.city,
      region: form.address.region,
      zip: form.address.zip,
      country: countryCode,
    },
  };

  try {
    setIsSaving(true);

    const res = await apiFetch(
      `/api/accounting/payers/${payer.payer_id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      showError(txt || "Save failed");
      setIsSaving(false);
      return;
    }

    showSuccess("Payer saved");
    onSaved && onSaved();
    onClose();
  } catch (e) {
    console.error(e);
    showError("Failed to save payer");
  } finally {
    setIsSaving(false);
  }
};


  if (!isOpen || !payer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>

        <h2 className="text-xl text-[#3A4149] font-semibold mb-4">Edit Payer</h2>
        <div className="h-[1px] bg-[#a15d33] mb-4"></div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm text-[#00000080] mb-1">Name *</label>
            <input
              required
              className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-[#00000080] mb-1">Legal Name *</label>
            <input
              required
              className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
              value={form.legalName}
              onChange={e => handleChange('legalName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-[#00000080] mb-1">Worker Type *</label>
            <select
              required
              className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
              value={form.workerType}
              onChange={e => handleChange('workerType', e.target.value)}
            >
              <option value="">Select type</option>
              <option value="Outsourced">Outsourced Agency</option>
              <option value="Employee">Employee - field staff</option>
              <option value="Contractor">Contractor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#DC1F00] mb-1">Address *</label>
           <AddressAutocomplete
  value={form.address}
  onChange={(addr) =>
    setForm((prev) => {
      if (!addr || typeof addr !== "object") {
        return {
          ...prev,
          address: {
            name: "",
            street: "",
            city: "",
            region: "",
            zip: "",
            country: "",
          },
        };
      }
      return {
        ...prev,
        address: {
          ...prev.address,
          ...addr,
          country: addr.country ?? prev.address.country ?? "",
          name: addr.name ?? prev.address.name ?? "",
        },
      };
    })
  }
/>
          </div>
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full border-2 border-[#DC1F00] text-[#DC1F00] hover:bg-[#DC1F001A] px-4 py-2 rounded-full"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-[#DC1F00] hover:bg-[#b81a00] text-white px-4 py-2 rounded-full disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPayerModal;
